// Bridge: three-way reconciliation of disk (the mirror), crdt (meta + content
// Y.Docs), and shadow (last-synced snapshot) — plugin spec §Bridge. It owns
// the shadow table and drives every disk edit and every remote change through
// a serialized queue so ops never interleave or tear.
//
// The whole engine reduces to two idempotent operations that converge when
// run repeatedly:
//   reconcileDiskPath(path)   — disk → crdt (local creates/edits/renames/deletes)
//   reconcileMetaEntry(fileId) — crdt → disk (remote creates/renames/deletes)
// Content pulls route through reconcileDiskPath (which also does the both-
// changed merge). Startup reconcile is just running both over the union of
// disk ∪ shadow ∪ crdt (plugin spec §Startup reconcile).
//
// Serialization note (decisions/20): v1 uses one global serial queue rather
// than per-file queues. That is strictly stronger than the spec's per-file +
// meta-lock scheme (no interleaving at all), so the invariant the spec asks
// for — ops don't tear — holds; per-file parallelism is a deferred perf
// optimization.

import * as Y from "yjs";
import { Mode } from "@cairn/proto";
import type { ClockPort, FileStat, PersistencePort, ShadowEntry, VaultPort } from "./ports";
import type { DocStore } from "./docStore";
import { META_DOC_ID, META_SYNC_TIMEOUT_MS } from "./docStore";
import type { Provider } from "./provider";
import { AttachmentsClient, hashBytes } from "./attachments";
import { Emitter } from "./emitter";
import { MetaView, contentText, type FileEntry } from "./meta";
import { threeWayMergeInto } from "./textDiff";
import { ulid, type UlidSource, systemUlidSource } from "./ulid";
import {
  conflictName,
  isIgnored,
  isTextFile,
  isValidPath,
  pathKey,
  sameFile,
} from "./conflictPolicy";
import type { MergeNotice, RestoreAffordance, ConflictNotice, ReconcileError } from "./events";

const encoder = new TextEncoder();

/** blake3 hex of a markdown file's UTF-8 text (the shadow's contentHash). */
function hashText(text: string): string {
  return hashBytes(encoder.encode(text).buffer as ArrayBuffer);
}

/** A serial executor keyed by a string; same key runs strictly in order. */
class KeyedQueue {
  private tails = new Map<string, Promise<unknown>>();
  run<T>(key: string, fn: () => Promise<T>): Promise<T> {
    const prev = this.tails.get(key) ?? Promise.resolve();
    const next = prev.then(fn, fn);
    // Swallow rejections on the chain tail so one failure can't wedge the key.
    this.tails.set(
      key,
      next.then(
        () => undefined,
        () => undefined,
      ),
    );
    return next as Promise<T>;
  }
}

export interface BridgeOptions {
  /** ULID source (injectable for deterministic tests). */
  ulidSource?: UlidSource;
  /** Budget for the blocking meta-doc sync in `start()` (default 30s). */
  metaSyncTimeoutMs?: number;
  /**
   * Budget for the best-effort per-content-doc pre-sync at startup (default
   * 10s). A doc that doesn't sync within it is left for its later `synced`
   * event to reconcile — which, like every other reconcile, is serialized
   * through the single reconcile queue (see `start()`), so it can never
   * double-apply against the startup pass.
   */
  startupContentSyncTimeoutMs?: number;
  /** Steady-state reconcile-retry knobs (C3). Test-tunable. */
  reconcileRetry?: {
    baseMs?: number;
    capMs?: number;
    maxAttempts?: number;
  };
}

export class Bridge {
  private meta: MetaView;
  private readonly shadowByPath = new Map<string, ShadowEntry>();
  private readonly queue = new KeyedQueue();
  private readonly ulidSource: UlidSource;
  private readonly metaSyncTimeoutMs: number;
  private readonly startupContentSyncTimeoutMs: number;
  private readonly retryBaseMs: number;
  private readonly retryCapMs: number;
  private readonly maxReconcileAttempts: number;
  private started = false;
  private stopped = false;
  /**
   * The post-meta-sync startup work (content pre-sync, reactive wiring, the
   * startup reconcile pass, upload resume, event pump), started exactly once the
   * meta doc first syncs — whether that happens within `start()`'s inline budget
   * or later, after the provider reconnects to a server that was unreachable at
   * launch. Shared so the inline path and the deferred `synced` completer await
   * the same run. Non-null once kicked off. See `start()` / `completeStartup()`.
   */
  private startupCompletion: Promise<void> | null = null;
  private eventPump: Promise<void> | null = null;
  private signalStop: () => void = () => {};
  private readonly stopSignal = new Promise<void>((resolve) => {
    this.signalStop = resolve;
  });

  /** Content hashes the bridge just wrote, to swallow the resulting FS echo. */
  private readonly expectedContent = new Map<string, string>();
  /**
   * Paths currently bound to a live editor (the adapter's CM6 binding). While
   * bound, the editor ↔ Y.Text link is the content channel for that file: FS
   * modify events only *adopt* disk states that already equal the CRDT text
   * (the editor's auto-save), and content pulls never write the file — the
   * binding feeds the editor, whose auto-save produces the adopting event.
   * Without this, the editor's edits would be applied twice (once via the
   * binding, once via the disk diff). decisions/21-obsidian-adapter.md.
   */
  private readonly editorBound = new Set<string>();
  private pendingRenames: Array<{ from: string; to: string }> = [];
  private renameFlush: ReturnType<typeof setTimeout> | null = null;
  /** Docs mid epoch-rebase — normal content pulls must not clobber the disk. */
  private readonly pendingRebase = new Set<string>();
  /** Attachment fileIds with an out-of-band download in flight (dedupe). */
  private readonly downloading = new Set<string>();
  private readonly expectedRemovals = new Set<string>();
  private readonly expectedRenames = new Set<string>(); // key `${from}\0${to}` (NUL-separated)

  /** Event surfaces for the UI (adapter + viewer). */
  readonly mergeNotices = new Emitter<MergeNotice>();
  readonly restoreAffordances = new Emitter<RestoreAffordance>();
  readonly conflictNotices = new Emitter<ConflictNotice>();
  readonly reconcileErrors = new Emitter<ReconcileError>();

  /** Paths whose steady-state reconcile is failing, with attempt counts, so a
   *  failure is retried + surfaced rather than silently swallowed (C3). */
  private readonly reconcileRetry = new Map<string, number>();

  constructor(
    private readonly vault: VaultPort,
    private readonly persistence: PersistencePort,
    private readonly docs: DocStore,
    private readonly provider: Provider,
    private readonly clock: ClockPort,
    private readonly attachments: AttachmentsClient,
    opts: BridgeOptions = {},
  ) {
    this.meta = new MetaView(this.docs.getDoc(META_DOC_ID) ?? new Y.Doc());
    this.ulidSource = opts.ulidSource ?? systemUlidSource;
    this.metaSyncTimeoutMs = opts.metaSyncTimeoutMs ?? META_SYNC_TIMEOUT_MS;
    this.startupContentSyncTimeoutMs = opts.startupContentSyncTimeoutMs ?? 10_000;
    this.retryBaseMs = opts.reconcileRetry?.baseMs ?? 500;
    this.retryCapMs = opts.reconcileRetry?.capMs ?? 30_000;
    this.maxReconcileAttempts = opts.reconcileRetry?.maxAttempts ?? 6;
  }

  private metaView(): MetaView {
    const doc = this.docs.getDoc(META_DOC_ID);
    if (doc && doc !== this.meta.doc) {
      // The meta doc was reset (epoch change); rebind.
      this.meta = new MetaView(doc);
    }
    return this.meta;
  }

  /**
   * Promote a content doc to LIVE (the adapter calls this when a file opens
   * in the editor) or demote it back to BACKGROUND on close. Awareness/cursor
   * relays only flow to LIVE subscribers (protocol spec §Connection lifecycle).
   */
  promote(fileId: string): void {
    this.provider.setMode(fileId, Mode.MODE_LIVE);
  }
  demote(fileId: string): void {
    this.provider.setMode(fileId, Mode.MODE_BACKGROUND);
  }

  /**
   * Mark `path` as bound to a live editor (see `editorBound`). The adapter
   * calls this when a CM6 binding attaches; on rename it re-binds under the
   * new path.
   */
  bindEditor(path: string): void {
    this.editorBound.add(path);
  }

  /**
   * Release an editor binding. Schedules one reconcile so any disk/CRDT drift
   * accumulated while bound (e.g. an auto-save the pump missed) converges.
   */
  unbindEditor(path: string): void {
    if (!this.editorBound.delete(path)) return;
    if (this.started && !this.stopped) this.scheduleDiskReconcile(path);
  }

  /**
   * Begin reconciling. Subscribe the meta doc and, once it first syncs, run the
   * startup reconcile and start streaming events.
   *
   * The initial meta sync is NON-TERMINAL: if the server is unreachable at
   * launch the inline wait below times out but does NOT throw. The provider
   * keeps reconnecting (backoff + `wake()`), and a one-shot `synced` completer
   * runs the deferred startup the moment meta first syncs — self-healing, no
   * reload required. Previously this threw on the meta-sync timeout and the
   * caller tore the engine down, leaving it dead until the user reopened
   * Obsidian (decisions/84). `start()` still awaits full startup when the server
   * IS reachable, so a caller that then sweeps editors sees a converged vault.
   */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // Load the shadow table.
    for (const e of await this.persistence.getShadow()) {
      this.shadowByPath.set(e.path, e);
    }

    // Complete startup as soon as the meta doc first syncs — whether that is
    // within the inline budget below or later, once the provider reconnects to
    // a server that was down at launch. The completer unsubscribes itself and
    // `completeStartup` is guarded to run exactly once.
    const offFirstMetaSync = this.docs.synced.on((docId) => {
      if (docId !== META_DOC_ID) return;
      offFirstMetaSync();
      void this.completeStartup();
    });

    // Meta doc first (LIVE), then try to sync it so we can read the tree.
    await this.docs.subscribe(META_DOC_ID, Mode.MODE_LIVE);
    try {
      await this.docs.waitSynced(META_DOC_ID, this.metaSyncTimeoutMs);
    } catch {
      // Server unreachable / slow: don't fail. The provider keeps reconnecting
      // and `offFirstMetaSync` runs the deferred startup once meta syncs. Status
      // stays connecting/offline (never a terminal error) and auto-recovers.
      return;
    }
    // Meta synced within budget: `offFirstMetaSync` fired (or is about to) and
    // started `completeStartup`; await the same shared run so `start()` resolves
    // only after the full startup pass completes.
    await this.completeStartup();
  }

  /** Run the post-meta-sync startup work exactly once (shared across the inline
   *  and deferred completers). */
  private completeStartup(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.startupCompletion) return this.startupCompletion;
    this.startupCompletion = this.runStartupAfterMeta();
    return this.startupCompletion;
  }

  /** Content pre-sync, reactive wiring, the startup reconcile pass, upload
   *  resume, and the event pump — everything that needs a synced meta tree. */
  private async runStartupAfterMeta(): Promise<void> {
    if (this.stopped) return;

    // Subscribe every active content doc (BACKGROUND) and wait for their sync
    // so the both-changed merge can read remote text.
    const active = this.metaView().active();
    for (const e of active) {
      if (e.kind === "md") await this.docs.subscribe(e.fileId, Mode.MODE_BACKGROUND);
    }
    for (const e of active) {
      if (e.kind === "md") {
        try {
          await this.docs.waitSynced(e.fileId, this.startupContentSyncTimeoutMs);
        } catch {
          /* a slow doc will reconcile when it later syncs — serialized through
             the same queue as startup, so no double-apply (see below) */
        }
      }
    }

    // React to remote changes and epoch resets.
    this.docs.remoteChange.on(({ docId, isMeta }) => {
      if (this.stopped) return;
      if (isMeta) this.scheduleMetaReconcile();
      else if (!this.pendingRebase.has(docId)) this.scheduleContentReconcile(docId);
    });
    this.docs.synced.on((docId) => {
      if (this.stopped) return;
      if (docId === META_DOC_ID) {
        // A meta (re)sync after a reconnect may have merged remote entries via
        // the SYNC EXCHANGE, which fires no `remoteChange` (the doc isn't yet
        // `synced` when those updates apply). Reconcile the merged meta — in
        // particular, resolve path collisions from concurrent same-path creates
        // that would otherwise never be elected (C2).
        this.scheduleMetaReconcile();
        return;
      }
      // A doc mid-rebase must not take the normal pull path — that would
      // overwrite the on-disk pending edit before the rebase preserves it.
      if (!this.pendingRebase.has(docId)) this.scheduleContentReconcile(docId);
    });
    this.docs.epochChanged.on((docId) => {
      if (this.stopped || docId === META_DOC_ID) return;
      // Epoch rebase (plugin spec §Recovery): after the resync, reapply the
      // on-disk edits so disk text is never lost. Marked synchronously here so
      // the pull path above is suppressed until the rebase completes.
      this.pendingRebase.add(docId);
      this.scheduleEpochRebase(docId);
    });
    this.docs.rejected.on(({ docId }) => {
      if (this.stopped || docId === META_DOC_ID) return;
      // A doc-scoped rejection (e.g. TOO_LARGE) makes docStore drop the local
      // Y.Doc/outbox and full-resync — the SAME loss of local state as an epoch
      // reset, but the server's resynced text is OLDER than the user's on-disk
      // edit (the rejected update never landed). Without arming the rebase, the
      // post-resync `synced` classifies that older text as remote-only and
      // pulls it OVER the edited disk file, violating disk-never-lost (C1). Arm
      // pending-rebase synchronously (before fullResync's resubscribe) and
      // reapply the on-disk edit as a fresh local edit, exactly like epoch
      // recovery. Emitted synchronously by `onRejected` before it kicks off the
      // resync, so the guard is in place before any pull can run.
      this.pendingRebase.add(docId);
      this.scheduleEpochRebase(docId);
    });

    // Run the startup pass THROUGH the reconcile queue, not alongside it. The
    // reactive listeners above are already live and schedule their work via
    // `queue.run` — a content doc that timed out in the pre-sync loop and syncs
    // now would otherwise reconcile the same path CONCURRENTLY with the startup
    // pass's own reconcile of it, and two overlapping three-way merges of one
    // path double-apply the disk diff (patch_apply isn't idempotent under
    // concurrency — the same hazard decisions/21 cites for the editor path).
    // Queuing startup keeps everything in the one serialization domain, so a
    // late sync's reconcile simply runs after startup and sees a converged
    // shadow (M2).
    await this.queue.run("", () => this.startupReconcile());
    // Re-queue any attachment uploads left owed by a previous session before we
    // start streaming events (N1); startup already ran, so shadows are settled.
    await this.resumePendingUploads();
    this.pumpEvents();
  }

  /** Reconcile disk ∪ crdt ∪ shadow after an offline period (spec matrix). */
  async startupReconcile(): Promise<void> {
    const meta = this.metaView();

    // Resolve path collisions across the merged meta before materializing, so a
    // concurrent same-path create from another client can't land two entries on
    // one disk path (C2).
    await this.resolveCollisions();
    const active = meta.active();

    // crdt → disk for every active entry (remote creates/renames).
    for (const e of active) {
      await this.reconcileMetaEntry(e.fileId);
    }
    // disk → crdt for every non-ignored disk file (local creates/edits).
    const listed = await this.vault.list();
    const diskPaths = new Set<string>();
    for (const stat of listed) {
      if (isIgnored(stat.path)) continue;
      diskPaths.add(stat.path);
      // Pass the stat we already have so reconcile doesn't re-`list()` the whole
      // vault per path — that made startup O(files²) (m5).
      await this.reconcileDiskPath(stat.path, stat);
    }
    // Shadow paths absent on both sides (deleted while offline everywhere).
    for (const path of [...this.shadowByPath.keys()]) {
      if (!diskPaths.has(path)) await this.reconcileDiskPath(path);
    }

    // Drop echo tokens recorded by the writes above: the vault event listener
    // isn't registered until pumpEvents (right after this), so those echoes are
    // never delivered on the real adapter and their tokens would leak forever.
    // The shadow is already updated, so any echo that *does* arrive no-ops via
    // the shadow compare in reconcileDiskPath — the token is pure optimization
    // (m6).
    this.clearEchoTokens();
  }

  // --- steady-state event streaming ---------------------------------------

  private pumpEvents(): void {
    this.eventPump = (async () => {
      const iter = this.vault.events()[Symbol.asyncIterator]();
      const stop = this.stopSignal.then(() => "stop" as const);
      while (!this.stopped) {
        const next = await Promise.race([iter.next(), stop]);
        if (next === "stop" || this.stopped) break;
        if (next.done) break;
        this.handleFsEvent(next.value);
      }
      await iter.return?.();
    })();
  }

  private handleFsEvent(ev: {
    type: "create" | "modify" | "rename" | "delete";
    path: string;
    oldPath?: string;
  }): void {
    if (isIgnored(ev.path)) return;
    switch (ev.type) {
      case "rename": {
        const from = ev.oldPath ?? "";
        if (this.expectedRenames.delete(`${from}\0${ev.path}`)) return;
        // Same-kind renames batch into ONE meta transaction so a folder rename
        // (a burst of per-child events) flips the tree atomically for remote
        // observers (DESIGN §1: structure can't tear). Kind-changing renames
        // are tombstone+create, handled individually.
        if (isTextFile(from) === isTextFile(ev.path)) this.enqueueRename(from, ev.path);
        else this.scheduleRename(from, ev.path);
        break;
      }
      case "delete":
        if (this.expectedRemovals.delete(ev.path)) return;
        this.scheduleDiskReconcile(ev.path);
        break;
      default: // create | modify
        this.scheduleDiskReconcile(ev.path);
    }
  }

  private scheduleDiskReconcile(path: string): void {
    void this.queue.run("", () => this.guarded(path, () => this.reconcileDiskPath(path)));
  }
  private scheduleMetaReconcile(): void {
    void this.queue.run("", async () => {
      // Resolve path collisions across the merged meta first, so materialization
      // below never lands two entries on one disk path (C2).
      await this.resolveCollisions();
      // Guard EACH entry so one failing path (vault I/O, etc.) neither aborts the
      // whole pass nor is silently swallowed — it is retried + surfaced (C3).
      for (const e of this.metaView().all()) {
        await this.guarded(e.path, () => this.reconcileMetaEntry(e.fileId));
      }
    });
  }
  private scheduleContentReconcile(fileId: string): void {
    void this.queue.run("", async () => {
      const entry = this.metaView().get(fileId);
      if (entry && entry.deletedAt === undefined) {
        await this.guarded(entry.path, () => this.reconcileDiskPath(entry.path));
      }
    });
  }

  // --- reconcile failure policy (C3) --------------------------------------
  // Scheduled reconcile work used to discard its promise, so a failed vault
  // write / shadow persistence / rename produced no retry, no status, and no
  // divergence signal. Every guarded reconcile now surfaces a failure, retries
  // with capped exponential backoff, and gives up loudly (never silently).

  /**
   * Run a reconcile op for `path`, surfacing + retrying any failure (C3). A
   * success here does NOT clear a prior failure: a *different* scheduler may
   * no-op successfully (e.g. a content reconcile that can't write because the
   * meta pull is what failed), which must not mask the real, unresolved
   * divergence. Only the retry path — which re-runs the converging operation
   * and whose success therefore implies convergence — clears the failure.
   */
  private async guarded(path: string, fn: () => Promise<void>): Promise<void> {
    // A path already in a failure state (including one that gave up as
    // `permanent`) re-runs through the converge-or-throw retry, so a genuine
    // success — not a scheduler no-op — clears it and emits `resolved`. This is
    // how a permanent failure self-heals once its cause is fixed and any later
    // reconcile of the path lands (verification minor).
    if (this.reconcileRetry.has(path)) {
      await this.retryReconcile(path);
      return;
    }
    try {
      await fn();
    } catch (err) {
      this.handleReconcileFailure(path, err);
    }
  }

  private clearReconcileFailure(path: string): void {
    if (this.reconcileRetry.delete(path)) {
      this.reconcileErrors.emit({ path, attempts: 0, permanent: false, resolved: true });
    }
  }

  private handleReconcileFailure(path: string, err: unknown): void {
    const prev = this.reconcileRetry.get(path) ?? 0;
    // Already gave up on this path: stay surfaced, but don't keep incrementing/
    // re-emitting from other schedulers (that would be the silent loop again).
    if (prev >= this.maxReconcileAttempts) return;
    const attempts = prev + 1;
    this.reconcileRetry.set(path, attempts);
    const message = err instanceof Error ? err.message : String(err);
    const permanent = attempts >= this.maxReconcileAttempts;
    this.reconcileErrors.emit({ path, message, attempts, permanent, resolved: false });
    if (permanent || this.stopped) return; // surfaced persistently; stop looping
    const delay = Math.min(this.retryCapMs, this.retryBaseMs * 2 ** (attempts - 1));
    void this.clock.timer(delay).then(() => {
      if (this.stopped || !this.reconcileRetry.has(path)) return;
      void this.queue.run("", () => this.retryReconcile(path));
    });
  }

  /** A retry attempt: re-run both reconcile directions; success here means the
   *  path actually reconverged (a diverged create/pull re-attempts its write, a
   *  converged path no-ops), so it's safe to clear the failure. */
  private async retryReconcile(path: string): Promise<void> {
    if (this.stopped || !this.reconcileRetry.has(path)) return;
    try {
      await this.reReconcilePath(path);
      this.clearReconcileFailure(path);
    } catch (err) {
      this.handleReconcileFailure(path, err);
    }
  }

  /** Re-run both reconcile directions for a path (a failed remote create needs
   *  reconcileMetaEntry; a failed local edit/pull needs reconcileDiskPath). */
  private async reReconcilePath(path: string): Promise<void> {
    if (this.stopped) return;
    const entry = this.metaView().byPath(path);
    if (entry) await this.reconcileMetaEntry(entry.fileId);
    await this.reconcileDiskPath(path);
  }
  private scheduleRename(from: string, to: string): void {
    void this.queue.run("", () => this.handleLocalRename(from, to));
  }

  /** Collect a same-kind rename; flush the whole burst as one meta txn. */
  private enqueueRename(from: string, to: string): void {
    this.pendingRenames.push({ from, to });
    if (this.renameFlush) return;
    // A macrotask timer, so an entire folder-rename burst (all delivered as
    // microtask-resolved FS events) accumulates before we commit.
    this.renameFlush = setTimeout(() => {
      this.renameFlush = null;
      const batch = this.pendingRenames;
      this.pendingRenames = [];
      void this.queue.run("", () => this.applyRenameBatch(batch));
    }, 0);
  }

  /** Apply a batch of same-kind renames as a single atomic meta transaction. */
  private async applyRenameBatch(batch: Array<{ from: string; to: string }>): Promise<void> {
    if (this.stopped) return;
    const meta = this.metaView();
    const moves: Array<{ shadow: ShadowEntry; to: string }> = [];
    const deferred: Array<{ from: string; to: string }> = [];
    // One transaction updates every path entry, so observers see the whole
    // tree flip at once.
    this.docs.transactLocal(META_DOC_ID, () => {
      for (const { from, to } of batch) {
        const shadow = this.shadowByPath.get(from);
        const entry = shadow ? meta.get(shadow.fileId) : undefined;
        if (shadow && entry && entry.deletedAt === undefined) {
          meta.setPath(shadow.fileId, to);
          moves.push({ shadow, to });
        } else {
          deferred.push({ from, to });
        }
      }
    });
    for (const { shadow, to } of moves) {
      await this.dropShadow(shadow.path);
      await this.writeShadow({ ...shadow, path: to });
    }
    // Renames whose source wasn't a known synced file fall back individually.
    for (const { from, to } of deferred) await this.handleLocalRename(from, to);
  }

  private scheduleEpochRebase(fileId: string): void {
    void this.queue.run("", () => this.epochRebase(fileId));
  }

  // --- shadow helpers ------------------------------------------------------

  private shadowFor(fileId: string): ShadowEntry | undefined {
    for (const s of this.shadowByPath.values()) if (s.fileId === fileId) return s;
    return undefined;
  }

  private async writeShadow(e: ShadowEntry): Promise<void> {
    this.shadowByPath.set(e.path, e);
    await this.persistence.putShadow(e);
  }

  private async dropShadow(path: string): Promise<void> {
    this.shadowByPath.delete(path);
    await this.persistence.deleteShadow(path);
  }

  // --- echo-suppression tokens ---------------------------------------------
  // Recorded before a bridge disk write, consumed by the matching FS event.
  // If that event never arrives (a coalesced/dropped save, or a write during
  // startupReconcile before the vault listener is registered), the token would
  // otherwise linger forever. So each set is size-capped (oldest evicted) and
  // cleared at the end of startupReconcile — the shadow re-compare in
  // reconcileDiskPath is the real backstop, making a dropped token a no-op
  // rather than a wrong-swallow (m6).

  private static readonly MAX_ECHO_TOKENS = 8192;

  private expectContent(path: string, hash: string): void {
    this.expectedContent.set(path, hash);
    this.capOldest(this.expectedContent);
  }
  private expectRename(from: string, to: string): void {
    // NUL separator (matches the delete-site key): a NUL can't occur in a valid
    // path, so `from`/`to` are unambiguous even when a path contains spaces.
    this.expectedRenames.add(`${from}\0${to}`);
    this.capOldest(this.expectedRenames);
  }
  private expectRemoval(path: string): void {
    this.expectedRemovals.add(path);
    this.capOldest(this.expectedRemovals);
  }

  /** Evict oldest entries (insertion order) until within the token cap. */
  private capOldest(coll: Map<string, unknown> | Set<string>): void {
    while (coll.size > Bridge.MAX_ECHO_TOKENS) {
      const oldest = coll.keys().next().value;
      if (oldest === undefined) break;
      coll.delete(oldest);
    }
  }

  private clearEchoTokens(): void {
    this.expectedContent.clear();
    this.expectedRenames.clear();
    this.expectedRemovals.clear();
  }

  // --- disk → crdt ---------------------------------------------------------

  /** Reconcile the disk file at `path` against crdt + shadow (the matrix).
   *  `knownStat` (from the startup listing) avoids a per-path `vault.list()`. */
  private async reconcileDiskPath(path: string, knownStat?: FileStat): Promise<void> {
    if (this.stopped || isIgnored(path)) return;
    const meta = this.metaView();
    const shadow = this.shadowByPath.get(path);
    const disk = await this.readDisk(path, knownStat);

    if (disk) {
      // Echo of the bridge's own write → adopt as synced, do nothing else.
      if (this.expectedContent.get(path) === disk.hash) {
        this.expectedContent.delete(path);
        if (shadow) {
          const adopted: ShadowEntry = {
            ...shadow,
            contentHash: disk.hash,
            lastSyncedMtime: disk.mtime,
          };
          // The adopted disk text IS the last-synced text — refresh the merge
          // base too, so a later disk edit diffs against what was written.
          if (disk.isText && disk.text !== undefined) adopted.baseText = disk.text;
          await this.writeShadow(adopted);
        }
        return;
      }

      if (!shadow) {
        await this.createLocalFile(path, disk);
        return;
      }
      const entry = meta.get(shadow.fileId);
      if (entry === undefined && shadow.uploadPending) {
        // Orphan upload-pending marker: a hard crash between writing the marker
        // and committing the attachment's meta entry (marker-first ordering,
        // N1). The disk file still holds the bytes, so recover by dropping the
        // stale marker and re-running the local create — never mistake it for a
        // remote delete, which would remove the user's file. Any blob already
        // PUT for the abandoned id is unreferenced and the server GCs it.
        await this.dropShadow(shadow.path);
        await this.createLocalFile(path, disk);
        return;
      }
      if (!entry || entry.deletedAt !== undefined) {
        // Remote deleted while we still hold it: delete-vs-edit / delete-sync.
        await this.resolveRemoteDelete(shadow, disk);
        return;
      }
      if (entry.kind === "attachment") {
        await this.reconcileAttachmentDisk(entry, shadow, disk);
        return;
      }
      await this.reconcileMdContent(entry, shadow, disk);
      return;
    }

    // Disk absent.
    if (!shadow) return; // never synced; nothing to do
    const entry = meta.get(shadow.fileId);
    if (!entry || entry.deletedAt !== undefined) {
      await this.dropShadow(shadow.path); // gone on both sides
      return;
    }
    // Local delete of a still-live file.
    await this.propagateLocalDelete(entry, shadow);
  }

  private async createLocalFile(
    path: string,
    disk: { hash: string; mtime: number; isText: boolean; text?: string; bytes?: ArrayBuffer },
  ): Promise<void> {
    if (!isValidPath(path)) return;
    const meta = this.metaView();

    // Same-file dedupe (DESIGN §14): an NFD/NFC variant of an existing remote
    // file is the *same* file, not a new one — adopt its id, never duplicate.
    const same = meta.active().find((e) => e.path !== path && sameFile(e.path, path));
    if (same) {
      await this.adoptExistingFile(path, same, disk);
      return;
    }

    const fileId = ulid(this.ulidSource);
    const kind = disk.isText ? "md" : "attachment";

    // Path-collision (rule 1): lower ULID keeps the path, the other renames.
    const colliding = meta.collidingWith(path);
    let usePath = path;
    if (colliding) {
      const shortId = fileId.slice(-6);
      if (fileId < colliding.fileId) {
        // We keep the path; rename the existing (converges — both clients
        // compute the same loser deterministically).
        const losePath = conflictName(colliding.path, colliding.fileId.slice(-6));
        this.docs.transactLocal(META_DOC_ID, () => meta.setPath(colliding.fileId, losePath));
        this.conflictNotices.emit({
          originalPath: colliding.path,
          renamedTo: losePath,
          fileId: colliding.fileId,
        });
      } else {
        usePath = conflictName(path, shortId);
        this.conflictNotices.emit({ originalPath: path, renamedTo: usePath, fileId });
        if (usePath !== path) {
          this.expectRename(path, usePath);
          await this.vault.rename(path, usePath);
        }
      }
    }

    if (kind === "attachment") {
      const bytes = disk.bytes ?? (await this.vault.readBinary(usePath));
      // MARKER-FIRST ordering (N1): persist the durable upload-pending shadow
      // BEFORE committing/sending the meta entry. The two are separate IDB
      // writes, so a hard crash between them must fail safe. Marker-first leaves
      // at worst an orphan marker (shadow, no meta entry) — recovered on restart
      // by re-running the create, with any already-PUT blob left unreferenced
      // for the server to GC. Meta-first would instead leave a committed,
      // server-visible reference with no marker, permanently re-stranding the
      // bytes (they'd never be re-uploaded). The hash is content-addressed and
      // known locally, so the shadow can be written before the meta commit.
      await this.writeShadow({
        path: usePath,
        fileId,
        contentHash: disk.hash,
        lastSyncedMtime: disk.mtime,
        uploadPending: true,
      });
      this.docs.transactLocal(META_DOC_ID, () => meta.add(fileId, usePath, "attachment", disk.hash));
      this.startUpload(disk.hash, bytes);
      return;
    }

    // md: meta entry + content doc from disk text.
    const text = disk.text ?? (await this.vault.readText(usePath));
    this.docs.transactLocal(META_DOC_ID, () => meta.add(fileId, usePath, "md"));
    await this.docs.subscribe(fileId, Mode.MODE_BACKGROUND);
    this.docs.transactLocal(fileId, (doc) => {
      const t = contentText(doc);
      if (t.length > 0) t.delete(0, t.length);
      t.insert(0, text);
    });
    await this.writeShadow({
      path: usePath,
      fileId,
      contentHash: hashText(text),
      lastSyncedMtime: disk.mtime,
      baseText: text,
    });
  }

  /**
   * A local disk file is the same file as an existing remote entry (NFD/NFC
   * variant): adopt the remote id rather than minting a new one. Link the
   * shadow and union any content difference — never a duplicate entry.
   */
  private async adoptExistingFile(
    path: string,
    entry: FileEntry,
    disk: { hash: string; mtime: number; text?: string; bytes?: ArrayBuffer },
  ): Promise<void> {
    if (entry.kind === "attachment") {
      await this.writeShadow({
        path,
        fileId: entry.fileId,
        contentHash: entry.hash ?? disk.hash,
        lastSyncedMtime: disk.mtime,
      });
      return;
    }
    const doc = await this.ensureContentDoc(entry.fileId);
    const ytext = contentText(doc);
    const crdtText = ytext.toString();
    const diskText = disk.text ?? "";
    if (diskText !== crdtText) {
      let merged = "";
      this.docs.transactLocal(entry.fileId, () => {
        merged = threeWayMergeInto(ytext, "", diskText);
      });
      await this.writeMergedToDisk(
        entry,
        { path, fileId: entry.fileId, contentHash: "", lastSyncedMtime: disk.mtime },
        merged,
        "merge",
      );
      return;
    }
    await this.writeShadow({
      path,
      fileId: entry.fileId,
      contentHash: hashText(diskText),
      lastSyncedMtime: disk.mtime,
      baseText: diskText,
    });
  }

  private async reconcileMdContent(
    entry: FileEntry,
    shadow: ShadowEntry,
    disk: { hash: string; mtime: number; text?: string },
  ): Promise<void> {
    const doc = await this.ensureContentDoc(entry.fileId);
    const ytext = contentText(doc);
    const crdtText = ytext.toString();
    const crdtHash = hashText(crdtText);
    const diskText = disk.text ?? "";
    const base = shadow.baseText ?? "";
    const diskChanged = disk.hash !== shadow.contentHash;
    const crdtChanged = crdtHash !== shadow.contentHash;

    if (!diskChanged && !crdtChanged) return;

    if (this.editorBound.has(entry.path)) {
      // Editor-bound: the CM6 binding already carries edits between the
      // editor and the Y.Text in both directions. A disk state equal to the
      // CRDT text is the editor's auto-save — adopt it as synced. Anything
      // else is a stale intermediate save, or a remote change the binding has
      // pushed into the editor whose auto-save hasn't landed yet — never
      // re-diff or overwrite it; the next save event adopts. If the app dies
      // before that save, the shadow still points at the last adopted state,
      // so startup reconcile replays the change as a plain pull/push.
      if (diskText === crdtText) {
        await this.writeShadow({
          ...shadow,
          contentHash: crdtHash,
          lastSyncedMtime: disk.mtime,
          baseText: crdtText,
        });
      }
      return;
    }

    if (!diskChanged && crdtChanged) {
      // Remote edit only → pull to disk.
      await this.pullTextToDisk(entry, shadow, crdtText, ytext);
      return;
    }

    // Disk changed (with or without a concurrent crdt change): merge our disk
    // edit into the crdt three-way. `merged === diskText` iff the crdt hadn't
    // moved (a pure push); otherwise it's the both-changed merge.
    let merged = "";
    this.docs.transactLocal(entry.fileId, () => {
      merged = threeWayMergeInto(ytext, base, diskText);
    });
    if (crdtChanged && merged !== diskText) {
      // Both changed: write the merged text back to disk + surface a notice.
      await this.writeMergedToDisk(entry, shadow, merged, "merge");
    } else {
      await this.writeShadow({
        ...shadow,
        contentHash: hashText(merged),
        lastSyncedMtime: disk.mtime,
        baseText: merged,
      });
    }
  }

  /** Pull crdt text to disk, detecting a concurrent disk edit (ifUnchangedSince). */
  private async pullTextToDisk(
    entry: FileEntry,
    shadow: ShadowEntry,
    text: string,
    ytext: Y.Text,
  ): Promise<void> {
    const path = entry.path;
    this.expectContent(path, hashText(text));
    const res = await this.vault.writeText(path, text, {
      ifUnchangedSince: shadow.lastSyncedMtime,
    });
    if (res.status === "conflict") {
      // Disk changed under us: three-way merge the concurrent disk edit into
      // the crdt, then rewrite (plugin spec §steady-state table).
      this.expectedContent.delete(path);
      const diskNow = await this.vault.readText(path);
      let merged = "";
      this.docs.transactLocal(entry.fileId, () => {
        merged = threeWayMergeInto(ytext, shadow.baseText ?? "", diskNow);
      });
      await this.writeMergedToDisk(entry, shadow, merged, "merge");
      return;
    }
    await this.writeShadow({
      ...shadow,
      path,
      contentHash: hashText(text),
      lastSyncedMtime: res.mtime,
      baseText: text,
    });
  }

  private async writeMergedToDisk(
    entry: FileEntry,
    shadow: ShadowEntry,
    merged: string,
    cause: "merge" | "rebase",
  ): Promise<void> {
    this.expectContent(entry.path, hashText(merged));
    const res = await this.vault.writeText(entry.path, merged, {});
    const mtime = res.status === "written" ? res.mtime : this.clock.now();
    await this.writeShadow({
      ...shadow,
      path: entry.path,
      contentHash: hashText(merged),
      lastSyncedMtime: mtime,
      baseText: merged,
    });
    this.mergeNotices.emit({
      path: entry.path,
      fileId: entry.fileId,
      at: this.clock.now(),
      cause,
    });
  }

  private async propagateLocalDelete(entry: FileEntry, shadow: ShadowEntry): Promise<void> {
    // Delete wins in meta (DESIGN §1 rule 3). Tombstone and drop shadow; a
    // concurrent remote editor's client offers the restore on its side.
    this.docs.transactLocal(META_DOC_ID, () =>
      this.metaView().tombstone(entry.fileId, Math.floor(this.clock.now() / 1000)),
    );
    await this.dropShadow(shadow.path);
  }

  private async resolveRemoteDelete(
    shadow: ShadowEntry,
    disk: { hash: string; mtime: number; text?: string; bytes?: ArrayBuffer },
  ): Promise<void> {
    const diskChanged = disk.hash !== shadow.contentHash;
    if (!diskChanged) {
      // Clean remote delete: remove from disk.
      this.expectRemoval(shadow.path);
      await this.vault.remove(shadow.path);
      await this.dropShadow(shadow.path);
      return;
    }
    // Delete-vs-edit, we hold the edit: never lose disk text. Keep the file,
    // drop the shadow (now unsynced), and offer to re-add it (restore).
    await this.dropShadow(shadow.path);
    this.restoreAffordances.emit({
      path: shadow.path,
      fileId: shadow.fileId,
      deletedBy: "remote",
      preservedText: disk.text,
    });
  }

  // --- crdt → disk ---------------------------------------------------------

  /**
   * Deterministic path-collision resolution over the whole merged meta state
   * (Codex C2). The local-insert election in `createLocalFile` only fires when
   * this client mints an entry — but two clients that concurrently create the
   * same normalized/case/NFC-equivalent path (offline) merge into two entries
   * with duplicate authoritative paths, which no local insert ever elected.
   * Run whenever merged meta changes: for each colliding group the lowest ULID
   * keeps the path; every other entry is a loser. Only the loser's OWNER (the
   * client holding its shadow) writes the conflict rename — each file has
   * exactly one owner, so there's no dueling rename, and `conflictName` is
   * deterministic so every client converges on the identical result. The disk
   * file is moved now (same pass) so a colliding winner can materialize at the
   * freed path without clobbering the loser's bytes. Non-owning clients leave
   * the loser alone; `reconcileMetaEntry` refuses to materialize a loser at the
   * colliding path until the owner's rename arrives.
   */
  private async resolveCollisions(): Promise<void> {
    if (this.stopped) return;
    const meta = this.metaView();
    const byKey = new Map<string, FileEntry[]>();
    for (const e of meta.active()) {
      const arr = byKey.get(pathKey(e.path));
      if (arr) arr.push(e);
      else byKey.set(pathKey(e.path), [e]);
    }
    for (const group of byKey.values()) {
      if (group.length < 2) continue;
      let winner = group[0] as FileEntry;
      for (const e of group) if (e.fileId < winner.fileId) winner = e;
      for (const loser of group) {
        if (this.stopped) return;
        if (loser.fileId === winner.fileId) continue;
        const shadow = this.shadowFor(loser.fileId);
        if (!shadow) continue; // only the loser's owner writes the rename
        const confPath = conflictName(loser.path, loser.fileId.slice(-6));
        if (pathKey(confPath) === pathKey(loser.path)) continue; // no-op rename
        this.docs.transactLocal(META_DOC_ID, () => meta.setPath(loser.fileId, confPath));
        this.conflictNotices.emit({
          originalPath: loser.path,
          renamedTo: confPath,
          fileId: loser.fileId,
        });
        // Move disk + shadow now (so a colliding winner can materialize at the
        // freed path). Guard the I/O in the same failure policy — keyed by the
        // NEW path, which the entry now occupies, so a retry/reconcile of that
        // path retries the disk move (verification minor). A throw here must not
        // abort the whole resolution pass or be swallowed.
        await this.guarded(confPath, () => this.applyRemoteRename(shadow, confPath));
      }
    }
  }

  /** Whether `entry` loses a path collision to a live entry with a lower ULID
   *  (which keeps the path). Such an entry must not be materialized at the
   *  colliding path — it will be renamed by its owner (see resolveCollisions). */
  private losesPathCollision(entry: FileEntry): boolean {
    const key = pathKey(entry.path);
    return this.metaView()
      .active()
      .some((e) => e.fileId !== entry.fileId && e.fileId < entry.fileId && pathKey(e.path) === key);
  }

  private async reconcileMetaEntry(fileId: string): Promise<void> {
    if (this.stopped) return;
    const meta = this.metaView();
    const entry = meta.get(fileId);
    if (!entry) return;
    const shadow = this.shadowFor(fileId);

    if (entry.deletedAt !== undefined) {
      // Remote tombstone.
      if (!shadow) return;
      const disk = await this.readDisk(shadow.path);
      if (!disk || disk.hash === shadow.contentHash) {
        if (disk) {
          this.expectRemoval(shadow.path);
          await this.vault.remove(shadow.path);
        }
        await this.dropShadow(shadow.path);
      } else {
        await this.resolveRemoteDelete(shadow, disk);
      }
      return;
    }

    // Collision loser: another live entry with a lower ULID keeps this path.
    // Don't materialize (or rename to) the colliding path — the loser's owner
    // renames it deterministically (resolveCollisions), and we materialize once
    // that rename lands (C2). If we OWN it, resolveCollisions already renamed it
    // before this loop, so this only gates a not-yet-renamed remote loser.
    if (this.losesPathCollision(entry)) return;

    if (!shadow) {
      await this.createRemoteFile(entry);
      return;
    }
    if (shadow.path !== entry.path) {
      // Remote rename.
      await this.applyRemoteRename(shadow, entry.path);
    }
    // Content pulls are handled by reconcileDiskPath when the content doc
    // changes; nothing else to do here.
  }

  private async createRemoteFile(entry: FileEntry): Promise<void> {
    const dir = entry.path.includes("/")
      ? entry.path.slice(0, entry.path.lastIndexOf("/"))
      : "";
    if (dir) await this.vault.mkdirp(dir);

    if (entry.kind === "attachment") {
      if (!entry.hash) return;
      // Download out-of-band; the reconcile queue continues immediately. When
      // the bytes arrive, the write + shadow update re-enter the queue so they
      // stay serialized with every other reconcile (M1).
      this.startDownload(entry.fileId, entry.hash);
      return;
    }
    // md: read the content doc (subscribe + sync first).
    const doc = await this.ensureContentDoc(entry.fileId);
    const ytext = contentText(doc);
    const crdtText = ytext.toString();

    // Merge-on-join: a local (still-unsynced) file already occupies this path
    // with different content. Union the local text into the remote content
    // doc — never overwrite (plugin spec §Merge-on-join: same path, differing
    // content is a char-merge, no conflict file, no data loss).
    const existing = await this.readDisk(entry.path);
    if (existing && existing.isText && existing.text !== undefined && existing.text !== crdtText) {
      let merged = "";
      this.docs.transactLocal(entry.fileId, () => {
        merged = threeWayMergeInto(ytext, "", existing.text as string);
      });
      await this.writeMergedToDisk(entry, {
        path: entry.path,
        fileId: entry.fileId,
        contentHash: "",
        lastSyncedMtime: existing.mtime,
      }, merged, "merge");
      return;
    }

    const text = crdtText;
    this.expectContent(entry.path, hashText(text));
    const res = await this.vault.writeText(entry.path, text, {});
    const mtime = res.status === "written" ? res.mtime : this.clock.now();
    await this.writeShadow({
      path: entry.path,
      fileId: entry.fileId,
      contentHash: hashText(text),
      lastSyncedMtime: mtime,
      baseText: text,
    });
  }

  private async applyRemoteRename(shadow: ShadowEntry, newPath: string): Promise<void> {
    const dir = newPath.includes("/") ? newPath.slice(0, newPath.lastIndexOf("/")) : "";
    if (dir) await this.vault.mkdirp(dir);
    this.expectRename(shadow.path, newPath);
    await this.vault.rename(shadow.path, newPath);
    await this.dropShadow(shadow.path);
    await this.writeShadow({ ...shadow, path: newPath });
  }

  // --- local rename --------------------------------------------------------

  private async handleLocalRename(from: string, to: string): Promise<void> {
    if (this.stopped) return;
    const shadow = this.shadowByPath.get(from);
    if (!shadow) {
      // Unknown source — treat the destination as a fresh disk file.
      await this.reconcileDiskPath(to);
      return;
    }
    const meta = this.metaView();
    const entry = meta.get(shadow.fileId);
    if (!entry) {
      await this.dropShadow(from);
      await this.reconcileDiskPath(to);
      return;
    }
    const fromKind = isTextFile(from);
    const toKind = isTextFile(to);
    if (fromKind !== toKind) {
      // Kind-changing rename (.md ↔ .txt): tombstone the old id, create anew
      // (plugin spec §steady-state table).
      this.docs.transactLocal(META_DOC_ID, () =>
        meta.tombstone(shadow.fileId, Math.floor(this.clock.now() / 1000)),
      );
      await this.dropShadow(from);
      await this.reconcileDiskPath(to);
      return;
    }
    // Same-kind rename: one meta path update, shadow key move.
    this.docs.transactLocal(META_DOC_ID, () => meta.setPath(shadow.fileId, to));
    await this.dropShadow(from);
    await this.writeShadow({ ...shadow, path: to });
  }

  // --- attachments ---------------------------------------------------------

  private async reconcileAttachmentDisk(
    entry: FileEntry,
    shadow: ShadowEntry,
    disk: { hash: string; mtime: number; bytes?: ArrayBuffer },
  ): Promise<void> {
    if (disk.hash === shadow.contentHash) return;
    // Disk attachment changed → last-writer-wins: update meta with the new
    // (locally-known) hash + shadow now, and upload the bytes out-of-band.
    const bytes = disk.bytes ?? (await this.vault.readBinary(entry.path));
    this.docs.transactLocal(META_DOC_ID, () => this.metaView().setHash(entry.fileId, disk.hash));
    await this.writeShadow({
      ...shadow,
      contentHash: disk.hash,
      lastSyncedMtime: disk.mtime,
      uploadPending: true,
    });
    this.startUpload(disk.hash, bytes);
  }

  /**
   * Hand a byte upload to the out-of-band retry queue and, on a confirmed
   * server commit, clear the durable `uploadPending` marker (N1). Fire-and-
   * forget — never awaited on the reconcile queue.
   */
  private startUpload(hash: string, bytes: ArrayBuffer): void {
    void this.attachments.queueUpload(hash, bytes, () => this.clearUploadPending(hash));
  }

  /** Clear the `uploadPending` flag on every shadow whose content is `hash`
   *  (content-addressed, so identical-content files share one upload). Runs on
   *  the reconcile queue so it can't tear with a concurrent shadow write. */
  private async clearUploadPending(hash: string): Promise<void> {
    await this.queue.run("", async () => {
      if (this.stopped) return;
      for (const shadow of [...this.shadowByPath.values()]) {
        if (shadow.uploadPending && shadow.contentHash === hash) {
          await this.writeShadow({ ...shadow, uploadPending: false });
        }
      }
    });
  }

  /**
   * Re-queue attachment uploads that were still owed at the last shutdown/crash
   * (durable `uploadPending` shadow flag, N1). Re-reads the bytes from disk and
   * verifies the hash so a since-changed file isn't mis-uploaded; upload dedup
   * by hash makes this a no-op when startupReconcile already re-queued it.
   */
  private async resumePendingUploads(): Promise<void> {
    for (const shadow of [...this.shadowByPath.values()]) {
      if (this.stopped) return;
      if (!shadow.uploadPending) continue;
      const entry = this.metaView().get(shadow.fileId);
      const disk = await this.readDisk(shadow.path);
      if (
        entry !== undefined &&
        entry.deletedAt === undefined &&
        disk &&
        !disk.isText &&
        disk.bytes &&
        disk.hash === shadow.contentHash
      ) {
        this.startUpload(shadow.contentHash, disk.bytes);
      } else if (entry === undefined || entry.deletedAt !== undefined) {
        // Orphan marker (no live meta entry): startupReconcile normally
        // re-creates or drops it, but tolerate it here too — drop the shadow so
        // it can't linger. Any already-PUT blob is unreferenced (server GCs it).
        await this.dropShadow(shadow.path);
      } else {
        // The file changed/was removed/became text since the marker — the
        // pending upload is moot (the current disk state reconciles on its own).
        await this.writeShadow({ ...shadow, uploadPending: false });
      }
    }
  }

  /**
   * Kick off an out-of-band download for a remote attachment. Deduped per
   * fileId so a repeated meta reconcile (before the shadow exists) doesn't
   * launch parallel downloads. On success the bytes are written through the
   * reconcile queue (see `applyDownloadedAttachment`); a permanent/exhausted
   * failure is surfaced via the attachment `failed` count.
   */
  private startDownload(fileId: string, hash: string): void {
    if (this.stopped || this.downloading.has(fileId)) return;
    this.downloading.add(fileId);
    void this.attachments
      .queueDownload(hash, (bytes) => this.applyDownloadedAttachment(fileId, hash, bytes))
      .finally(() => this.downloading.delete(fileId));
  }

  /** Write a freshly-downloaded attachment to disk, serialized through the
   *  reconcile queue and re-checked against the (possibly moved) meta entry. */
  private async applyDownloadedAttachment(
    fileId: string,
    hash: string,
    bytes: ArrayBuffer,
  ): Promise<void> {
    await this.queue.run("", async () => {
      if (this.stopped) return;
      const entry = this.metaView().get(fileId);
      // The entry may have been renamed, deleted, or re-hashed while the
      // download ran, or another pass may have already materialized it.
      if (
        !entry ||
        entry.deletedAt !== undefined ||
        entry.kind !== "attachment" ||
        entry.hash !== hash ||
        this.shadowFor(fileId)
      ) {
        return;
      }
      const dir = entry.path.includes("/")
        ? entry.path.slice(0, entry.path.lastIndexOf("/"))
        : "";
      if (dir) await this.vault.mkdirp(dir);
      this.expectContent(entry.path, hashBytes(bytes));
      const res = await this.vault.writeBinary(entry.path, bytes);
      const mtime = res.status === "written" ? res.mtime : this.clock.now();
      await this.writeShadow({
        path: entry.path,
        fileId,
        contentHash: hash,
        lastSyncedMtime: mtime,
      });
    });
  }

  // --- epoch rebase --------------------------------------------------------

  private async epochRebase(fileId: string): Promise<void> {
    try {
      await this.epochRebaseInner(fileId);
    } finally {
      this.pendingRebase.delete(fileId);
      // Whatever the crdt/disk state is now, reconcile once normally so a
      // genuine remote change that raced the rebase still lands.
      if (!this.stopped) this.scheduleContentReconcile(fileId);
    }
  }

  private async epochRebaseInner(fileId: string): Promise<void> {
    if (this.stopped || fileId === META_DOC_ID) return;
    // After a resync (epoch reset OR doc-scoped rejection, C1), the content doc
    // holds the server's text. Reapply the on-disk edits as a fresh local edit
    // so the user's text is never lost (plugin spec §Recovery paths).
    try {
      await this.docs.waitSynced(fileId, 15_000);
    } catch {
      return;
    }
    const entry = this.metaView().get(fileId);
    if (!entry || entry.deletedAt !== undefined) return;
    const shadow = this.shadowFor(fileId);
    if (!shadow) return;
    const disk = await this.readDisk(shadow.path);
    if (!disk || disk.text === undefined) return;
    const doc = this.docs.getDoc(fileId);
    if (!doc) return;
    const ytext = contentText(doc);
    const crdtText = ytext.toString();
    if (crdtText === disk.text) {
      await this.writeShadow({ ...shadow, contentHash: disk.hash, baseText: disk.text });
      return;
    }
    // Reapply the on-disk edits onto the resynced crdt (plugin spec §Recovery
    // paths). The resynced crdt text is the correct merge base — using the
    // shadow's base would be wrong, since a still-unsynced local edit
    // optimistically advanced it (for a rejection the shadow even advanced to
    // the rejected text). Diffing crdt-vs-disk reproduces the on-disk edit onto
    // the server's text; disk text is never lost.
    let merged = "";
    this.docs.transactLocal(fileId, () => {
      merged = threeWayMergeInto(ytext, crdtText, disk.text as string);
    });
    await this.writeMergedToDisk(entry, shadow, merged, "rebase");
  }

  // --- disk read + doc helpers --------------------------------------------

  private async ensureContentDoc(fileId: string): Promise<Y.Doc> {
    let doc = this.docs.getDoc(fileId);
    if (!doc) {
      doc = await this.docs.subscribe(fileId, Mode.MODE_BACKGROUND);
    }
    try {
      await this.docs.waitSynced(fileId, 15_000);
    } catch {
      /* proceed with whatever local state we have */
    }
    return doc;
  }

  private async readDisk(
    path: string,
    knownStat?: FileStat,
  ): Promise<
    | { hash: string; mtime: number; isText: boolean; text?: string; bytes?: ArrayBuffer }
    | null
  > {
    // Reuse a caller-supplied stat (startup pass) or, for one-off reconciles,
    // find it in a single fresh listing.
    let stat = knownStat;
    if (!stat) {
      const listed = await this.vault.list();
      stat = listed.find((s) => s.path === path);
    }
    if (!stat) return null;
    if (isTextFile(path)) {
      const text = await this.vault.readText(path);
      return { hash: hashText(text), mtime: stat.mtime, isText: true, text };
    }
    const bytes = await this.vault.readBinary(path);
    return { hash: hashBytes(bytes), mtime: stat.mtime, isText: false, bytes };
  }

  /** Execute a merge plan's file operations through the normal queue. */
  async applyPlanOperation(fn: () => Promise<void>): Promise<void> {
    await this.queue.run("", fn);
  }

  async stop(): Promise<void> {
    this.stopped = true;
    this.signalStop();
    await this.eventPump?.catch(() => undefined);
  }
}
